import { useMemo, useState, type ReactNode } from "react";
import { Calendar, Check, ChevronDown, History, Layers, User } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/others/Badges";
import { Card, CardContent } from "@/components/ui/library/card";
import { useAppSelector } from "@/stores/hook";
import { cn } from "@/lib/utils";
import { formatFollowupDate, formatFollowupTime } from "@/utils/date";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import { usePermissions } from "@/rbac/usePermissions";
import { useFollowUpTimeEnabled } from "@/features/leads/api/useLeadFormConfig";
import useRolesPipelineLite from "@/features/users/api/useRolesPipelineLite";
import { useV2FilterOptions } from "@/features/Dashboard/v2/api/useV2FilterOptions";
import useLeadPipeline from "@/features/leads/hook/useLeadPipeline";
import { buildEntityRejectionResolver } from "@/features/leads/utils/entityRejection";
import { formatRelativeFollowUp } from "@/features/leads/components/helperComponents/NextUpRow";
import {
  describeMutingDeal,
  readDealFollowUpMute,
} from "@/features/leads/utils/dealFollowUpMute";

/* ────────────────────────────────────────────────────────────────────── */
/* Types                                                                  */
/* ────────────────────────────────────────────────────────────────────── */

type Scope = "lead" | "child";

interface RoleSection {
  key: string;
  scope: Scope;
  roleId: number | null;
  label: string;
  holder: string | null;
  own: boolean;
  /** The slot's LIVE row (status Pending), if any. */
  live: any | null;
  /** Everything else — superseded/completed history, newest first. */
  history: any[];
  /** Rule 0 v2: no live row → the slot carries its LAST date (today if none). */
  virtualDate: string | null;
  /** Role takes follow-ups on this entity's CURRENT stage (RBAC dial ≠ Deny). */
  participates: boolean;
}

const isPendingStatus = (s: unknown) =>
  typeof s === "string" && s.toLowerCase() === "pending";

const TONE_CLASS: Record<ReturnType<typeof formatRelativeFollowUp>["tone"], string> = {
  overdue: "bg-[#FDECEC] text-[#B42318] border-[#F8C9C9]",
  today: "bg-[#FFF4E5] text-[#B25E09] border-[#FFDDB3]",
  soon: "bg-[#EAF3FF] text-[#1D4ED8] border-[#C7DDFF]",
  later: "bg-[#F3F4F6] text-[#4B5563] border-[#E5E7EB]",
};

/* ────────────────────────────────────────────────────────────────────── */
/* Row pieces — the original timeline entry, alignment fixed              */
/* ────────────────────────────────────────────────────────────────────── */
/*
 * This is the pre-2026-09-08 look the user asked to come back (rounded
 * calendar tile + 2px rail, big date, pill badges, grey note box). What is
 * NOT restored is its alignment: the old metadata block faked the indent of
 * its second line with `pl-4`, which never lined up with the text after the
 * icon, and its `h-8 / mt-2 / mb-4` rhythm left a hole above every note. Both
 * lines now sit in one `[14px_1fr]` grid, so "For …" and "Set by …" share a
 * left edge on every row of every section.
 */

/** Date (+ time when the tenant captures one). */
function DateText({ iso, dim }: { iso: string; dim?: boolean }) {
  const timeEnabled = useFollowUpTimeEnabled();
  const time = timeEnabled ? formatFollowupTime(iso) : "";
  return (
    <h4
      className={cn(
        "text-[13px] font-semibold leading-5",
        dim ? "text-[var(--color-black-7)]" : "text-[var(--color-black-9)]",
      )}
    >
      {formatFollowupDate(iso)}
      {time && (
        <>
          <span className="mx-1 font-normal text-[var(--color-black-5)]">·</span>
          <span className="font-medium text-[var(--color-black-7)]">{time}</span>
        </>
      )}
    </h4>
  );
}

function RelativePill({ iso }: { iso: string }) {
  const rel = formatRelativeFollowUp(iso);
  return (
    <span
      className={cn(
        "inline-flex h-4 shrink-0 items-center whitespace-nowrap rounded-full border px-1.5 text-[10px] font-semibold",
        TONE_CLASS[rel.tone],
      )}
    >
      {rel.label}
    </span>
  );
}

/** The two metadata lines, sharing one left edge (the old `pl-4` fix). */
function MetaLines({
  holderName,
  roleName,
  setBy,
  setByRole,
}: {
  holderName: string | null;
  roleName: string | null;
  setBy: string;
  setByRole?: string;
}) {
  return (
    <div className="mt-1.5 grid grid-cols-[14px_1fr] items-start gap-x-1 gap-y-[3px] text-[11px] leading-[1.45] text-[var(--color-black-6)]">
      <User className="mt-[2px] size-3 opacity-60" aria-hidden />
      <span className="min-w-0">
        <span className="opacity-80">For </span>
        <span className="font-medium text-[var(--color-black-8)]">
          {holderName ?? "Unassigned"}
        </span>
        {roleName && <span className="opacity-70"> · {roleName}</span>}
        {!holderName && roleName && (
          <span className="ml-1 font-medium text-[#b08a3e]">
            — no {roleName} assigned
          </span>
        )}
      </span>
      <span aria-hidden />
      <span className="min-w-0 opacity-80">
        Set by{" "}
        <span className="font-medium text-[var(--color-black-8)]">{setBy}</span>
        {setByRole && <span className="opacity-70"> ({setByRole})</span>}
      </span>
    </div>
  );
}

/** The timeline shell: calendar tile + rail, content column beside it. */
function TimelineEntry({
  isLast,
  dashed,
  dim,
  children,
}: {
  isLast: boolean;
  dashed?: boolean;
  dim?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="group flex gap-3">
      <div className="flex w-8 shrink-0 flex-col items-center">
        <div
          className={cn(
            "relative z-10 flex size-8 shrink-0 items-center justify-center rounded-[8px] bg-white",
            dashed
              ? "border border-dashed border-[var(--color-black-2)]"
              : "border border-[var(--color-black-1)] shadow-sm",
          )}
        >
          <Calendar
            className={cn(
              "size-4",
              dim ? "text-[var(--color-black-5)]" : "text-[var(--color-primary)]",
            )}
            strokeWidth={1.5}
          />
        </div>
        {!isLast && (
          <div className="my-0.5 w-[2px] flex-1 bg-[var(--color-black-1)] transition-colors group-hover:bg-[var(--color-black-2)]" />
        )}
      </div>
      <div className={cn("flex min-w-0 flex-1 flex-col", isLast ? "pb-0" : "pb-4")}>
        {children}
      </div>
    </div>
  );
}

/** Grey panel the note (and the completion footer) sits in. */
function NotePanel({ children }: { children: ReactNode }) {
  return (
    <div className="mt-2 rounded-[8px] border border-[#f3f4f6] bg-[#f9fafb] p-2.5">
      {children}
    </div>
  );
}

/** The one LIVE follow-up of a slot — a real Pending row. */
function LiveRow({
  followup,
  holderName,
  roleName,
  canEdit,
  registerFn,
  registerPath,
  formIndex,
  isLast,
}: {
  followup: any;
  holderName: string | null;
  roleName: string | null;
  canEdit: boolean;
  registerFn: any;
  registerPath: string;
  formIndex: number;
  isLast: boolean;
}) {
  const setBy = followup?.isSystem ? "System" : followup?.createdBy?.name || "System";
  const setByRole = followup?.createdBy?.role?.name || "";
  const editable = canEdit && !followup?.isSystem;
  const note = followup?.note?.note ?? "";

  return (
    <TimelineEntry isLast={isLast}>
      <div className="flex min-h-8 flex-wrap items-center gap-2">
        <DateText iso={followup.date} />
        <RelativePill iso={followup.date} />
        <Badge variant="newLead" className="h-4 shrink-0 px-1.5 text-[10px]">
          Pending
        </Badge>
        {roleName && (
          <Badge variant="choices" className="h-4 shrink-0 px-1.5 text-[10px]">
            {roleName}
          </Badge>
        )}
      </div>
      <MetaLines
        holderName={holderName}
        roleName={roleName}
        setBy={setBy}
        setByRole={setByRole}
      />
      {editable ? (
        <Textarea
          placeholder="Add a completion note…"
          defaultValue={note}
          {...registerFn(`${registerPath}.${formIndex}.note.note` as const)}
          className="mt-2 min-h-[46px] w-full resize-none rounded-[8px] border-[var(--color-black-2)] px-3 py-2 text-[13px] focus-visible:ring-1 focus-visible:ring-[var(--color-primary)]"
        />
      ) : (
        <NotePanel>
          <p className="text-[13px] leading-snug text-[var(--color-black-7)]">
            {note || (followup?.isSystem ? "Automated by System" : "No note added.")}
          </p>
        </NotePanel>
      )}
    </TimelineEntry>
  );
}

/**
 * VIRTUAL slot (rule 0 v2): no live row → the slot carries its LAST
 * follow-up date, today only if it never had one. Same date the card chip,
 * the dashboard and the rail use, so the tab never says "today" while the
 * card says "12 days overdue" (Revath Mohan, 2026-09-08).
 */
function VirtualRow({
  iso,
  hadHistory,
  holderName,
  roleName,
  isLast,
}: {
  iso: string;
  hadHistory: boolean;
  holderName: string | null;
  roleName: string | null;
  isLast: boolean;
}) {
  const rel = formatRelativeFollowUp(iso);
  return (
    <TimelineEntry isLast={isLast} dashed dim>
      <div className="flex min-h-8 flex-wrap items-center gap-2">
        <DateText iso={iso} dim />
        <RelativePill iso={iso} />
        <Badge variant="newLead" className="h-4 shrink-0 px-1.5 text-[10px]">
          Pending
        </Badge>
        {roleName && (
          <Badge variant="choices" className="h-4 shrink-0 px-1.5 text-[10px]">
            {roleName}
          </Badge>
        )}
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-black-5)]">
          auto
        </span>
      </div>
      <MetaLines
        holderName={holderName}
        roleName={roleName}
        setBy="System"
      />
      <NotePanel>
        <p className="text-[13px] leading-snug text-[var(--color-black-7)]">
          {hadHistory
            ? rel.tone === "overdue"
              ? "No follow-up scheduled — still counted on the last date until a new one is set."
              : "No follow-up scheduled — carries the last date until a new one is set."
            : "No follow-up scheduled yet — due today until one is set."}
        </p>
      </NotePanel>
    </TimelineEntry>
  );
}

/** A closed entry: same shell, "Completed" badge, completion footer. */
function HistoryRow({
  followup,
  holderName,
  roleName,
  isLast,
}: {
  followup: any;
  holderName: string | null;
  roleName: string | null;
  isLast: boolean;
}) {
  const setBy = followup?.isSystem ? "System" : followup?.createdBy?.name || "System";
  const setByRole = followup?.createdBy?.role?.name || "";
  const completedBy = followup?.completedBy?.name || "";
  const completedByRole = followup?.completedBy?.role?.name || "";
  const note: string = followup?.note?.note ?? "";

  return (
    <TimelineEntry isLast={isLast} dim>
      <div className="flex min-h-8 flex-wrap items-center gap-2">
        <DateText iso={followup.date} dim />
        <Badge variant="verified" className="h-4 shrink-0 px-1.5 text-[10px]">
          Completed
        </Badge>
        {roleName && (
          <Badge variant="choices" className="h-4 shrink-0 px-1.5 text-[10px]">
            {roleName}
          </Badge>
        )}
      </div>
      <MetaLines
        holderName={holderName}
        roleName={roleName}
        setBy={setBy}
        setByRole={setByRole}
      />
      <NotePanel>
        <p className="text-[13px] leading-snug text-[var(--color-black-9)]">
          {note || "No note added."}
        </p>
        {completedBy && (
          <div className="mt-2 flex items-center gap-1 border-t border-[#e5e7eb] pt-2 text-[11px] text-[var(--color-black-6)]">
            <Check className="size-3 text-[var(--color-success)]" strokeWidth={2.4} />
            <span className="opacity-80">Completed by:</span>
            <span className="font-medium text-[var(--color-black-8)]">{completedBy}</span>
            {completedByRole && <span className="opacity-70">({completedByRole})</span>}
          </div>
        )}
      </NotePanel>
    </TimelineEntry>
  );
}

const HISTORY_PREVIEW = 2;

function SectionBlock({
  section,
  grainLabel,
  canEdit,
  registerFn,
  registerPath,
  formIndexOf,
  expanded,
  onToggle,
}: {
  section: RoleSection;
  grainLabel: string;
  canEdit: boolean;
  registerFn: any;
  registerPath: string;
  formIndexOf: (fu: any) => number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const isRole = section.roleId != null;
  const hidden = Math.max(0, section.history.length - HISTORY_PREVIEW);
  const shown = expanded ? section.history : section.history.slice(0, HISTORY_PREVIEW);
  // The role each row belongs to. Role sections stamp their own label; the
  // pre-upgrade group carries whatever the row itself knows.
  const roleOf = (fu: any): string | null =>
    isRole ? section.label : (fu?.role?.name ?? null);
  const openRow = section.live ? "live" : isRole && section.virtualDate ? "virtual" : null;
  const lastIndex = shown.length - 1;

  return (
    <section className="flex flex-col">
      <header className="mb-3 flex items-center gap-2">
        <span className="shrink-0 text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--color-text-gray)]">
          {section.label}
          {section.own ? " (you)" : ""}
        </span>
        {isRole && (
          <span className="shrink-0 text-[11px] font-medium text-[var(--color-black-7)]">
            {section.holder ?? "Unassigned"}
          </span>
        )}
        {isRole && (
          <span className="inline-block max-w-[140px] shrink-0 truncate rounded border border-[var(--color-black-1)] px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-[var(--color-black-6)]">
            {grainLabel}
          </span>
        )}
        <span className="h-px flex-1 bg-[var(--color-black-1)]" />
      </header>

      {/* The NOT-completed follow-up ALWAYS leads the section — a real
          pending row, or the virtual slot when there is none. */}
      {openRow === "live" && (
        <LiveRow
          followup={section.live}
          holderName={section.holder}
          roleName={roleOf(section.live)}
          canEdit={canEdit}
          registerFn={registerFn}
          registerPath={registerPath}
          formIndex={formIndexOf(section.live)}
          isLast={shown.length === 0}
        />
      )}
      {openRow === "virtual" && (
        <VirtualRow
          iso={section.virtualDate!}
          hadHistory={section.history.length > 0}
          holderName={section.holder}
          roleName={isRole ? section.label : null}
          isLast={shown.length === 0}
        />
      )}
      {shown.map((fu, i) => (
        <HistoryRow
          key={fu.id ?? i}
          followup={fu}
          holderName={section.holder}
          roleName={roleOf(fu)}
          isLast={i === lastIndex}
        />
      ))}

      {hidden > 0 && (
        <button
          type="button"
          onClick={onToggle}
          className="ml-11 mt-1 inline-flex w-fit items-center gap-1 text-[11px] font-medium text-[var(--color-black-6)] hover:text-[var(--color-black-8)]"
        >
          <ChevronDown
            className={cn("size-3.5 transition-transform", expanded && "rotate-180")}
          />
          {expanded ? "Show less" : `${hidden} earlier`}
        </button>
      )}
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Main                                                                   */
/* ────────────────────────────────────────────────────────────────────── */

export default function FollowUp() {
  const { leadFolowUps, childFolowUps, activeData, isChildActive } =
    useActiveDataMutation();
  const { register: leadRegister, lead } = useLeadForm();
  const { register: childRegister } = useChildForm();
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set());
  const [showOffStage, setShowOffStage] = useState(false);

  const toggleExpanded = (key: string) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /**
   * The DETAIL endpoint ships assignments as `leadUsers`; the raw list shape
   * is `LeadUsers`. Read both (lead 401411, 2026-08-30).
   */
  const leadAssignRows = ((lead as any)?.leadUsers ??
    (lead as any)?.LeadUsers ??
    []) as any[];
  const childAssignRows = isChildActive
    ? (((activeData as any)?.childUsers ?? (activeData as any)?.ChildUsers ?? []) as any[])
    : [];

  /** CURRENT holder per role slot, per grain — live from the assignment rows. */
  const holderByRole = useMemo(() => {
    const build = (rows: any[]) => {
      const m = new Map<number, string>();
      for (const r of rows) {
        const rid = r?.user?.role?.id;
        if (rid != null && !m.has(rid) && r?.user?.name) m.set(Number(rid), r.user.name);
      }
      return m;
    };
    // A deal row of role R is held by the deal's R-assignee, else by the
    // LEAD's R-assignee (per-role fallback — the server's
    // `dealSlotAssigneePool`), so a lead-assigned Counsellor's row on a deal
    // worked by other roles reads "For <her>", not "no Counsellor assigned".
    // Deal rows first: `build` keeps the first holder seen per role.
    return {
      lead: build(leadAssignRows),
      child: build([...childAssignRows, ...leadAssignRows]),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead, isChildActive, activeData]);

  /**
   * Role catalogue — names AND follow-up participation. `forFilters:1` is the
   * hierarchy-scoped list (managers see the roles their hierarchy offers, the
   * viewer's own role always, admins everything). `followUpStageIds` is the
   * SAME set the dashboard, rail and leads filter gate on (server
   * `followup-role.config.ts`: personal stage ∧ Follow-ups dial ≠ Deny).
   */
  const { rolesPipeline } = useRolesPipelineLite({ forFilters: 1 });
  const roleCatalog = useMemo(() => {
    const m = new Map<number, { name: string; followUpStageIds: number[] | null }>();
    for (const r of (rolesPipeline ?? []) as any[]) {
      if (r?.id == null) continue;
      m.set(Number(r.id), {
        name: r.name ?? `Role #${r.id}`,
        followUpStageIds: Array.isArray(r.followUpStageIds)
          ? r.followUpStageIds.map(Number)
          : null,
      });
    }
    for (const lu of [...leadAssignRows, ...childAssignRows]) {
      const rid = lu?.user?.role?.id;
      if (rid != null && lu?.user?.role?.name && !m.has(Number(rid))) {
        m.set(Number(rid), { name: lu.user.role.name, followUpStageIds: null });
      }
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesPipeline, lead, activeData]);
  const roleName = (rid: number) => roleCatalog.get(rid)?.name ?? `Role #${rid}`;

  /**
   * RBAC IS THE SOURCE OF TRUTH (user directive 2026-09-08): a role whose
   * Follow-ups dial is Deny on the entity's CURRENT stage has no slot there —
   * the counsellor herself never sees it, so an admin must not see a
   * "Counsellor · due today" for it either. Unknown role / old server payload
   * / stage still loading → not gated (never hide on missing data).
   */
  const participates = (roleId: number, stageId: number): boolean => {
    const r = roleCatalog.get(roleId);
    if (!r || !Array.isArray(r.followUpStageIds)) return true;
    if (!Number.isFinite(stageId)) return true;
    return r.followUpStageIds.includes(stageId);
  };

  const { canFollowup, bypassesRbacChecks } = usePermissions();
  const {
    currentUser: { user },
  } = useAppSelector((state) => state.auth);

  /** Three-tier scope on the tab itself (user directive 2026-08-31). */
  const { isPersonalScopeViewer } = useV2FilterOptions();
  const ownRoleId = user?.role?.id != null ? Number(user.role.id) : null;
  const managerVisibleRoleIds = useMemo<Set<number> | null>(
    () =>
      !bypassesRbacChecks && Array.isArray(rolesPipeline)
        ? new Set((rolesPipeline as any[]).map((r) => Number(r.id)))
        : null,
    [bypassesRbacChecks, rolesPipeline],
  );
  const sectionVisible = (roleId: number | null) => {
    if (bypassesRbacChecks) return true;
    if (isPersonalScopeViewer && ownRoleId != null) return roleId === ownRoleId;
    if (roleId == null) return true; // legacy "Earlier" stays for managers
    return managerVisibleRoleIds === null || managerVisibleRoleIds.has(roleId);
  };

  /** Per-stage `lead_followup` gate for the VIEWER. */
  const stageIdOf = (entity: any): number => {
    const raw = entity?.stage?.id ?? entity?.stageId;
    if (raw == null || raw === "") return NaN;
    const n = Number(raw);
    return Number.isFinite(n) ? n : NaN;
  };
  const currentStageIdNum = stageIdOf(activeData);
  const leadStageId = stageIdOf(lead);
  const dealStageId = isChildActive ? stageIdOf(activeData) : NaN;
  const canViewFollowup = canFollowup(currentStageIdNum, "view");
  const canEditFollowup = canFollowup(currentStageIdNum, "edit");

  /** Rejected entity = no due follow-up shown (user directive 2026-09-01). */
  const { pipeline } = useLeadPipeline();
  const isEntityRejected = useMemo(() => buildEntityRejectionResolver(pipeline), [pipeline]);
  const leadRejected = isEntityRejected(lead);
  const activeDealRejected = isChildActive ? isEntityRejected(activeData) : false;

  /**
   * Rows normalised to always carry `role {id,name}` — the payload has carried
   * `roleId` since the role upgrade; the include arrives with newer servers.
   * The form binds completion notes by index into THIS array (formIndexOf), so
   * it is built once per grain and never filtered.
   */
  const normalise = (rows: any[] | undefined) =>
    (rows ?? []).map((fu) => {
      if (fu?.role?.name) return fu;
      const rid = fu?.role?.id ?? fu?.roleId;
      if (rid == null) return fu;
      return { ...fu, role: { id: Number(rid), name: roleName(Number(rid)) } };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const leadItems = useMemo(() => normalise(leadFolowUps), [leadFolowUps, roleCatalog]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const childItems = useMemo(() => normalise(childFolowUps), [childFolowUps, roleCatalog]);

  /**
   * Build the role sections of one grain. Rules, in order:
   *  1. every ASSIGNED role gets a section (a slot with no row is virtual);
   *  2. a section's NOT-completed row leads it, no matter its date; history
   *     follows newest-first;
   *  3. no live row → the slot carries its LAST date (today if never had one);
   *  4. rejected entity → no live/virtual row, history only;
   *  5. RBAC: a role that does not take follow-ups on the entity's current
   *     stage is moved out of the main list (its history is tucked away).
   */
  const buildSections = (
    scope: Scope,
    items: any[],
    assignRows: any[],
    stageId: number,
    rejected: boolean,
  ): RoleSection[] => {
    const groups = new Map<string, RoleSection>();
    const ensure = (rid: number | null, label: string): RoleSection => {
      const key = rid != null ? `${scope}-r${rid}` : `${scope}-legacy`;
      let g = groups.get(key);
      if (!g) {
        g = {
          key,
          scope,
          roleId: rid,
          label,
          holder: rid != null ? (holderByRole[scope].get(rid) ?? null) : null,
          own: rid != null && rid === ownRoleId,
          live: null,
          history: [],
          virtualDate: null,
          participates: rid == null ? true : participates(rid, stageId),
        };
        groups.set(key, g);
      }
      return g;
    };

    for (const fu of items) {
      if (fu?.isArchived) continue;
      const rid = fu?.role?.id != null ? Number(fu.role.id) : null;
      const g = ensure(rid, rid != null ? fu.role.name : "Earlier");
      if (isPendingStatus(fu?.status)) {
        // Rejected entity: pending rows are gate-invisible (kept in DB).
        if (rejected) continue;
        // Rule 1/3 guarantees one live row per slot; if two ever show up,
        // the EARLIEST due one leads (matches every "earliest pending" reader).
        if (!g.live || new Date(fu.date) < new Date(g.live.date)) {
          if (g.live) g.history.push(g.live);
          g.live = fu;
        } else {
          g.history.push(fu);
        }
      } else {
        g.history.push(fu);
      }
    }
    const assignedRoleIds = new Set<number>();
    for (const r of assignRows) {
      const rid = r?.user?.role?.id;
      if (rid == null) continue;
      assignedRoleIds.add(Number(rid));
      if (!rejected) ensure(Number(rid), r?.user?.role?.name ?? roleName(Number(rid)));
    }

    const todayIso = new Date().toISOString();
    for (const g of groups.values()) {
      g.history.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      // Rule 0 v2 — an ASSIGNED slot with no live row carries the role's
      // LAST row date (any non-archived status), today if it never had one.
      // A role nobody holds any more owes nothing: history only.
      if (!g.live && g.roleId != null && !rejected && assignedRoleIds.has(g.roleId)) {
        const last = g.history[0]?.date;
        g.virtualDate = last ? new Date(last).toISOString() : todayIso;
      }
    }
    // A group with nothing to show (e.g. a rejected entity whose only row
    // was the hidden pending one) renders no header.
    return [...groups.values()].filter(
      (g) => g.live || g.virtualDate || g.history.length > 0,
    );
  };

  const leadSections = buildSections("lead", leadItems, leadAssignRows, leadStageId, leadRejected);
  const dealSections = isChildActive
    ? buildSections("child", childItems, childAssignRows, dealStageId, activeDealRejected)
    : [];

  const visibleLead = leadSections.filter((g) => sectionVisible(g.roleId));
  const visibleDeal = dealSections.filter((g) => sectionVisible(g.roleId));

  /**
   * WHAT IS STILL OWED COMES FIRST (user directive 2026-09-09: "uncompleted
   * follow-ups should be on the top"). Ordered ACROSS grains — a completed
   * lead-level Counsellor slot no longer sits above an overdue deal one just
   * because lead sections used to render first. Tiers:
   *   0 a real Pending row · 1 a virtual date (nothing scheduled) · 2 history
   * Inside a tier the most OVERDUE leads, then the viewer's own role, then
   * lead before deal, then A→Z — so the order is stable and never arbitrary.
   * (This supersedes the 2026-08-30 "lead book first" rule, whose point was
   * that the Counsellor slot must not be buried: sorting by what is open
   * serves that better — an owed slot is now always near the top.)
   */
  const tierOf = (g: RoleSection) => (g.live ? 0 : g.virtualDate ? 1 : 2);
  const dueAt = (g: RoleSection) => {
    const iso = g.live?.date ?? g.virtualDate ?? g.history[0]?.date;
    const t = iso ? new Date(iso).getTime() : NaN;
    return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER;
  };
  const byUrgency = (a: RoleSection, b: RoleSection) => {
    const ta = tierOf(a);
    const tb = tierOf(b);
    if (ta !== tb) return ta - tb;
    if (ta !== 2 && dueAt(a) !== dueAt(b)) return dueAt(a) - dueAt(b);
    if (a.own !== b.own) return a.own ? -1 : 1;
    if (a.scope !== b.scope) return a.scope === "lead" ? -1 : 1;
    return a.label.localeCompare(b.label);
  };

  const onStage = [...visibleLead, ...visibleDeal]
    .filter((g) => g.roleId != null && g.participates)
    .sort(byUrgency);
  // Off-stage roles (RBAC Deny on the current stage): history only, tucked
  // away; their pending/virtual rows are NOT presented — nothing is due.
  const offStage = [
    ...visibleLead.filter((g) => g.roleId != null && !g.participates),
    ...visibleDeal.filter((g) => g.roleId != null && !g.participates),
  ]
    .map((g) => ({ ...g, live: null, virtualDate: null, history: g.live ? [g.live, ...g.history] : g.history }))
    .filter((g) => g.history.length > 0)
    .sort(byUrgency);
  const legacy = [...visibleLead, ...visibleDeal]
    .filter((g) => g.roleId == null && g.history.length + (g.live ? 1 : 0) > 0)
    .sort(byUrgency);

  if (!canViewFollowup) {
    return (
      <Card className="m-4 rounded-[12px] border-[var(--color-black-1)] py-4 shadow-none">
        <CardContent className="flex items-center justify-center py-8 text-[var(--color-black-6)]">
          <p className="text-xs font-medium">
            You don't have permission to view follow-ups on this stage.
          </p>
        </CardContent>
      </Card>
    );
  }

  const grainLabelOf = (scope: Scope) =>
    scope === "lead" ? "Lead" : (activeData as any)?.name || "Deal";
  const registerOf = (scope: Scope) => (scope === "lead" ? leadRegister : childRegister);
  const formIndexOf = (scope: Scope) => (fu: any) =>
    (scope === "lead" ? leadItems : childItems).indexOf(fu);

  const renderSection = (g: RoleSection) => (
    <SectionBlock
      key={g.key}
      section={g}
      grainLabel={grainLabelOf(g.scope)}
      canEdit={canEditFollowup}
      registerFn={registerOf(g.scope)}
      registerPath="followUps"
      formIndexOf={formIndexOf(g.scope)}
      expanded={expandedKeys.has(g.key)}
      onToggle={() => toggleExpanded(g.key)}
    />
  );

  const nothingAtAll = onStage.length + offStage.length + legacy.length === 0;

  // RULE 9 ("Prioritise latest deal"): the open deal's follow-ups are muted
  // by a newer sibling — the rows below are real, but no surface counts them.
  const dealMute = isChildActive ? readDealFollowUpMute(activeData) : null;

  return (
    <Card className="m-4 rounded-[12px] border-[var(--color-black-1)] py-2 shadow-none">
      <CardContent className="flex flex-col gap-4 p-3">
        {dealMute && (
          <div className="flex items-start gap-2.5 rounded-[10px] border border-[#C7DDFF] bg-[#EAF3FF] px-3 py-2.5">
            <span className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-[#1D4ED8]/10 text-[#1D4ED8]">
              <Layers className="size-3.5" />
            </span>
            <div className="min-w-0 leading-snug">
              <p className="text-[12px] font-semibold text-[#1D4ED8]">
                Follow-ups on this deal are muted
              </p>
              <p className="text-[11.5px] text-[#1e3a8a]">
                The lead's newest deal, {describeMutingDeal(dealMute)}, is the
                one being followed up — this stage prioritises the latest
                deal, so these rows don't appear in Overdue / Today / Later or
                ring reminders. They come back if that deal is rejected.
              </p>
            </div>
          </div>
        )}

        {nothingAtAll && (
          <p className="py-6 text-center text-xs font-medium text-[var(--color-black-6)]">
            No follow-up roles are assigned on this stage.
          </p>
        )}

        {/* What is still OWED first, whichever grain it sits on; closed
            slots follow. See `byUrgency`. */}
        {onStage.map(renderSection)}

        {/* Pre-upgrade rows (no role recorded) — history only, last. Labelled
            by grain: a lead AND a deal can each have one, and two identical
            "Earlier" headings told the reader nothing. */}
        {legacy.map((g) =>
          renderSection({
            ...g,
            label:
              g.scope === "lead" ? "Earlier · lead" : "Earlier · this deal",
            live: null,
            virtualDate: null,
            history: g.live ? [g.live, ...g.history] : g.history,
          }),
        )}

        {offStage.length > 0 && (
          <div className="border-t border-dashed border-[var(--color-black-1)] pt-2">
            <button
              type="button"
              onClick={() => setShowOffStage((v) => !v)}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[var(--color-black-6)] hover:text-[var(--color-black-8)]"
            >
              <History className="size-3.5" />
              {offStage.length === 1
                ? `${offStage[0].label} takes no follow-ups on this stage — ${offStage[0].history.length} earlier`
                : `${offStage.length} roles take no follow-ups on this stage — earlier history`}
              <ChevronDown
                className={cn("size-3.5 transition-transform", showOffStage && "rotate-180")}
              />
            </button>
            {showOffStage && (
              <div className="mt-3 flex flex-col gap-4 opacity-80">
                {offStage.map(renderSection)}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
